import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import { fetchBanners } from '../../features/ui/bannersSlice';
import LoadingSpinner from './LoadingSpinner';

interface BannerSliderProps {
  autoSlideInterval?: number; // بالثواني
  className?: string;
  showDots?: boolean;
  showArrows?: boolean;
  pauseOnHover?: boolean;
}

export default function BannerSlider({ 
  autoSlideInterval = 4, 
  className = "",
  showDots = true,
  showArrows = true,
  pauseOnHover = true
}: BannerSliderProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { banners, isLoading: loading, error } = useSelector((state: RootState) => state.banners);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Fetch banners on mount
  useEffect(() => {
    if (banners.length === 0) {
      dispatch(fetchBanners());
    }
  }, [dispatch, banners.length]);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // الحركة التلقائية للبانرات
  useEffect(() => {
    if (banners.length <= 1 || isPaused || isTransitioning) return;

    const interval = setInterval(() => {
      handleNext();
    }, autoSlideInterval * 1000);

    return () => clearInterval(interval);
  }, [banners.length, autoSlideInterval, isPaused, isTransitioning, currentIndex]);

  // التنقل مع تأثير الانتقال
  const handleSlideChange = (newIndex: number) => {
    if (isTransitioning || newIndex === currentIndex) return;
    
    setIsTransitioning(true);
    setCurrentIndex(newIndex);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 700);
  };

  const handleNext = () => {
    const nextIndex = currentIndex === banners.length - 1 ? 0 : currentIndex + 1;
    handleSlideChange(nextIndex);
  };

  const handlePrevious = () => {
    const prevIndex = currentIndex === 0 ? banners.length - 1 : currentIndex - 1;
    handleSlideChange(prevIndex);
  };





  if (loading) {
    return (
      <div className={`relative w-full ${className}`}>
        <div className="relative overflow-hidden rounded-xl shadow-lg">
          <div className="w-full h-40 md:h-45 lg:h-50 banner-skeleton" />
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner size="md" />
          </div>
        </div>
        {showDots && (
          <div className="flex justify-center mt-4 space-x-2">
            {[1, 2, 3].map((_, index) => (
              <div key={index} className="w-2.5 h-2.5 rounded-full bg-gray-300 animate-pulse" />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex justify-center items-center h-40 bg-gray-50 rounded-xl ${className}`}>
        <div className="text-center">
          <div className="text-4xl mb-2">📷</div>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (banners.length === 0) {
    return null;
  }

  return (
    <div className={`relative w-full ${className}`}>
      {/* البانر الحالي */}
      <div className="relative overflow-hidden rounded-xl shadow-lg bg-gray-50">
        <div 
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {banners.map((banner, index) => (
            <div key={banner.id} className="w-full flex-shrink-0 relative">
              <img
                src={banner.image}
                alt={`Banner ${banner.id}`}
                className="w-full h-38 md:h-45 lg:h-55 object-cover banner-image"
                loading={index === 0 ? "eager" : "lazy"}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                }}
              />
            </div>
          ))}
        </div>




      </div>




    </div>
  );
}